import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import { submitPhoto, verifyPhotoAdminPin } from '../lib/data.js'
import { TEST_PHOTO_TOURNAMENT_ID } from '../lib/teststation.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'

// Public photo / video submission page. Mobile-first.
//
//   /photos?t={tournamentId}                — public guest
//   /photos?t=…&role=admin&pin={pin}        — photo admin (auto-approve)
//   /photos?t=…&team={teamId}&hole={hole}   — pre-fills from scorecard
//
// Submissions track per-tournament against a localStorage counter so a
// single guest can't flood the wall (default 5). Photo admins bypass.
// Per-tournament submission counter with a rolling 16-hour reset window.
// First submission stamps `firstAt`; every subsequent read/write checks if
// 16 hours have elapsed and, if so, wipes the counter so the guest gets
// a fresh batch automatically (no admin action needed).
const PHOTO_LIMIT_WINDOW_MS = 16 * 60 * 60 * 1000
const countKey = (tid) => `photoSubmit_${tid}_count`
const firstAtKey = (tid) => `photoSubmit_${tid}_firstAt`

export default function PhotosApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')
  const teamId = params.get('team') || null
  const prefilledHole = params.get('hole') || ''
  const role = (params.get('role') || '').toLowerCase()
  const pin = params.get('pin') || ''

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [photoAdmin, setPhotoAdmin] = useState(null) // { name } when verified
  const [pinChecked, setPinChecked] = useState(false)

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the photo QR code.')
      return
    }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  // Verify photo admin PIN once on mount.
  useEffect(() => {
    if (!tournamentId) { setPinChecked(true); return }
    if (role !== 'admin' || !pin) { setPinChecked(true); return }
    let cancelled = false
    verifyPhotoAdminPin(tournamentId, pin).then((m) => {
      if (cancelled) return
      setPhotoAdmin(m)
      setPinChecked(true)
    })
    return () => { cancelled = true }
  }, [tournamentId, role, pin])

  const settings = tournament?.photoSettings || {}
  const maxPerGuest = Number(settings.maxPerGuest) || 5
  // Default-on: if no settings record exists, treat submissions as enabled.
  const submissionsEnabled = settings.enabled !== false
  const isPhotoAdmin = !!photoAdmin

  // Pre-fill submitter name from team if we have one.
  const teamName = useMemo(() => {
    if (!teamId || !tournament) return ''
    for (const f of Object.values(tournament.flights || {})) {
      const t = f?.teams?.[teamId]
      if (t) return t.info?.name || ''
    }
    return ''
  }, [teamId, tournament])

  return (
    <div className="min-h-full" style={{ backgroundColor: CLUB.backgroundColor }}>
      {/* Opened from the hub's Photo Submit → exit back to that hub (same
          event), not the welcome/landing. Scorecard-role submissions keep the
          normal history back so they return to the scorecard. */}
      {tournamentId && !teamId && !role ? (
        <a
          href={`/hub?t=${tournamentId}`}
          className="absolute top-3 left-3 z-30 rounded-lg px-2.5 py-1.5 text-sm font-extrabold uppercase tracking-wider transition active:scale-95 whitespace-nowrap"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', color: CLUB.accentColor, border: `1px solid ${CLUB.accentColor}66` }}>
          ← Back
        </a>
      ) : (
        <PageNav back home={false} tone="gold" />
      )}
      <div className="max-w-md mx-auto px-4 pt-14 pb-10">
        <Header tournament={tournament} isPhotoAdmin={isPhotoAdmin} adminName={photoAdmin?.name} />

        {error ? (
          <ErrorCard message={error} />
        ) : !tournament || !pinChecked ? (
          <p className="text-white/70 text-center mt-8">Loading…</p>
        ) : !submissionsEnabled && !isPhotoAdmin ? (
          <ErrorCard message="Photo submissions are currently closed. Check back soon!" />
        ) : (
          <SubmissionForm
            // Scorecard Test (Tile 2 on /teststation) routes its photo flow
            // through this same /photos page. When the active tournament is
            // flagged as a scorecard test station, redirect uploads to the
            // shared __testphotos__ store so they show up on /testphotowall
            // alongside photos submitted from /testphoto. Photos are also
            // auto-approved in that mode (no admin review step in tests).
            tournamentId={
              tournament?.info?.isTestStation && tournament?.info?.testStationTile === 'scorecard'
                ? TEST_PHOTO_TOURNAMENT_ID
                : tournamentId
            }
            isPhotoAdmin={
              isPhotoAdmin ||
              (tournament?.info?.isTestStation && tournament?.info?.testStationTile === 'scorecard')
            }
            teamId={teamId}
            teamName={teamName}
            prefilledHole={prefilledHole}
            adminName={photoAdmin?.name}
            maxPerGuest={maxPerGuest}
            // Video is restricted: only scorecard players (have a teamId) and
            // video-privileged staff (photo admin with canVideo) may submit it.
            // Public guests get photos only.
            videoAllowed={!!teamId || (isPhotoAdmin && !!photoAdmin?.canVideo)}
          />
        )}
      </div>
    </div>
  )
}

function Header({ tournament, isPhotoAdmin, adminName }) {
  return (
    <div className="text-center pt-2 pb-4">
      <div className="inline-block bg-white rounded-2xl px-5 py-3 shadow-lg" style={{ marginBottom: '12px' }}>
        <img src={CLUB.logo} alt={CLUB.name} style={{ height: '60px', objectFit: 'contain' }} />
      </div>
      <h1
        className="text-xl sm:text-2xl uppercase tracking-widest"
        style={{ color: CLUB.accentColor, fontFamily: 'Cinzel, serif', fontWeight: 600 }}>
        📸 Share Your Photos
      </h1>
      {tournament?.info?.name ? (
        <p className="text-white/85 mt-1 font-bold text-base">{tournament.info.name}</p>
      ) : null}
      {isPhotoAdmin ? (
        <div
          className="inline-block mt-2 px-3 py-1 rounded-full text-sm font-extrabold uppercase tracking-widest"
          style={{ backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }}>
          📸 Photo Admin Mode · {adminName}
        </div>
      ) : null}
    </div>
  )
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-2xl p-5 mt-8 text-center shadow-xl">
      <div className="text-3xl mb-2">⚠️</div>
      <p className="text-sm text-gray-700">{message}</p>
    </div>
  )
}

function SubmissionForm({ tournamentId, teamId, teamName, prefilledHole, isPhotoAdmin, adminName, maxPerGuest, videoAllowed }) {
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [videoDuration, setVideoDuration] = useState(null) // seconds, set after metadata loads
  const [name, setName] = useState(teamName || '')
  const [caption, setCaption] = useState('')
  const [hole, setHole] = useState(prefilledHole || '')
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)
  const [submittedCount, setSubmittedCount] = useState(() => readGuestCount(tournamentId))

  // Update name only when teamName resolves (it loads after mount).
  useEffect(() => {
    if (teamName && !name) setName(teamName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName])

  // Build a preview URL when file changes; revoke when it changes again.
  useEffect(() => {
    if (!file) { setPreviewUrl(''); return }
    const u = URL.createObjectURL(file)
    setPreviewUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])

  const guestLimitHit = !isPhotoAdmin && submittedCount >= maxPerGuest

  // Pick a file. For videos, probe duration via a hidden <video> element
  // and reject anything over 20 seconds before the user even sees a
  // preview — keeps oversized clips out of Storage entirely.
  const onPick = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFlash(null)
    setVideoDuration(null)
    // Video is gated: only scorecard players + video-privileged staff. Guard
    // here too in case the OS picker ignores the image-only `accept` hint.
    if (String(f.type || '').startsWith('video/') && !videoAllowed) {
      setFlash({ kind: 'err', msg: 'Video isn’t available here — please submit a photo.' })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (String(f.type || '').startsWith('video/')) {
      const probeUrl = URL.createObjectURL(f)
      const probe = document.createElement('video')
      probe.preload = 'metadata'
      probe.muted = true
      probe.src = probeUrl
      probe.onloadedmetadata = () => {
        const dur = Number(probe.duration) || 0
        URL.revokeObjectURL(probeUrl)
        if (dur > 20) {
          setFlash({
            kind: 'err',
            msg: `Video must be 20 seconds or less. Yours is ${Math.round(dur)} seconds — please trim it and resubmit.`,
          })
          if (fileInputRef.current) fileInputRef.current.value = ''
          setFile(null)
          setPreviewUrl('')
          setVideoDuration(null)
          return
        }
        setVideoDuration(dur)
        setFile(f)
      }
      probe.onerror = () => {
        URL.revokeObjectURL(probeUrl)
        setFlash({ kind: 'err', msg: 'Could not read this video file. Try a different one.' })
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
      return
    }
    setFile(f)
  }

  const reset = () => {
    setFile(null)
    setPreviewUrl('')
    setCaption('')
    setVideoDuration(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!file || busy) return
    if (guestLimitHit) return
    setBusy(true)
    setFlash(null)
    try {
      const role = isPhotoAdmin ? 'photoAdmin' : 'public'
      const submittedBy = (name || '').trim() || (isPhotoAdmin ? adminName || 'Photo Admin' : 'Guest')
      const meta = {
        submitterRole: role,
        submittedBy,
        approvedBy: isPhotoAdmin ? adminName || 'Photo Admin' : null,
        teamId: teamId || null,
        caption: (caption || '').trim(),
        hole: hole && hole !== 'clubhouse' ? Number(hole) : null,
        durationSeconds: videoDuration,
      }
      const res = await submitPhoto(tournamentId, file, meta)
      if (!isPhotoAdmin) {
        const next = recordGuestSubmission(tournamentId)
        setSubmittedCount(next)
      }
      setFlash({
        kind: 'ok',
        msg: res.status === 'approved'
          ? '✓ Photo added to the wall!'
          : '✓ Submitted! Your photo will appear after review.',
      })
      reset()
    } catch (err) {
      setFlash({ kind: 'err', msg: 'Upload failed: ' + (err?.message || String(err)) })
    } finally {
      setBusy(false)
      setTimeout(() => setFlash(null), 6000)
    }
  }

  if (guestLimitHit) {
    return (
      <div className="bg-white rounded-2xl p-5 mt-6 text-center shadow-xl">
        <div className="text-3xl mb-2">🎉</div>
        <p className="text-sm text-gray-700">
          You've reached the submission limit for this tournament. Thank you!
        </p>
      </div>
    )
  }

  const holes = ['clubhouse', ...Array.from({ length: 18 }, (_, i) => String(i + 1))]
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-5 mt-5 shadow-xl space-y-3">
      {/* Cross-platform file input. Notes:
          - `capture` is intentionally OMITTED. On iOS Safari, any `capture`
            value (including `environment`) forces the camera open and skips
            the Photo Library / Browse picker the user actually wants. With
            no `capture`, iOS shows the proper sheet ("Photo Library / Take
            Photo or Video / Choose Files") and Android Chrome shows its
            chooser with a Camera entry. Same attribute set works for both.
          - Visually hidden via off-screen position rather than display:none.
            Keeps the input in layout so input.click() reliably triggers the
            native picker on Android Chrome PWAs and older iOS WebKit.
          - accept=image/*,video/* preserves the existing video upload flow;
            the iOS sheet and Android chooser still appear correctly. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={videoAllowed ? 'image/*,video/*' : 'image/*'}
        onChange={onPick}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          left: -9999,
        }}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full rounded-xl px-4 py-6 text-center transition active:scale-[0.98]"
        style={{
          background: previewUrl
            ? '#f3f4f6'
            : `linear-gradient(180deg, ${CLUB.accentColor} 0%, ${CLUB.accentColorDark || CLUB.accentColor} 100%)`,
          color: previewUrl ? '#1f2937' : CLUB.primaryColor,
          border: previewUrl ? '2px dashed #9ca3af' : 'none',
          fontWeight: 800,
        }}>
        {previewUrl ? (
          file?.type?.startsWith('video/') ? (
            <div className="relative inline-block">
              <video src={previewUrl} className="max-h-56 mx-auto rounded-lg" controls />
              {videoDuration != null ? (
                <div
                  className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-sm font-extrabold"
                  style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff' }}>
                  {formatDuration(videoDuration)} ✓
                </div>
              ) : null}
            </div>
          ) : (
            <img src={previewUrl} alt="" className="max-h-56 mx-auto rounded-lg object-contain" />
          )
        ) : (
          <>
            <div className="text-4xl">📸</div>
            <div className="mt-2 text-base">Tap to take or pick a photo{videoAllowed ? ' / video' : ''}</div>
          </>
        )}
      </button>

      <div>
        <label className="text-sm uppercase tracking-widest text-gray-500 font-bold">Your name (optional)</label>
        <input
          className="input mt-1"
          placeholder="John Smith"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoCapitalize="words"
        />
      </div>

      <div>
        <label className="text-sm uppercase tracking-widest text-gray-500 font-bold">Caption (optional)</label>
        <input
          className="input mt-1"
          placeholder="Birdie on 7! 🦅"
          maxLength={100}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
      </div>

      <div>
        <label className="text-sm uppercase tracking-widest text-gray-500 font-bold">Where? (optional)</label>
        <select className="input mt-1" value={hole} onChange={(e) => setHole(e.target.value)}>
          <option value="">— Select —</option>
          {holes.map((h) => (
            <option key={h} value={h}>{h === 'clubhouse' ? 'Clubhouse' : `Hole ${h}`}</option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={busy || !file}
        className="w-full rounded-xl py-3.5 font-extrabold uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-40"
        style={{
          background: `linear-gradient(180deg, ${CLUB.accentColor} 0%, ${CLUB.accentColorDark || CLUB.accentColor} 100%)`,
          color: CLUB.primaryColor,
          boxShadow: '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
        }}>
        {busy ? 'Uploading…' : isPhotoAdmin ? 'Post to Wall' : 'Submit for Review'}
      </button>

      {flash ? (
        <p className={`text-sm font-bold text-center ${flash.kind === 'ok' ? 'text-green-deep' : 'text-red-600'}`}>
          {flash.msg}
        </p>
      ) : null}

      {!isPhotoAdmin ? (
        <p className="text-sm text-gray-500 text-center">
          {submittedCount}/{maxPerGuest} submitted from this device
        </p>
      ) : null}
    </form>
  )
}

// Format seconds as M:SS for the duration badge.
function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// Read the current submission count, transparently expiring stale records.
// If the rolling window has elapsed, both keys are cleared and 0 is returned
// — the next submission seeds a fresh `firstAt`.
function readGuestCount(tournamentId) {
  if (!tournamentId) return 0
  try {
    const firstAt = Number(localStorage.getItem(firstAtKey(tournamentId))) || 0
    if (firstAt && Date.now() - firstAt >= PHOTO_LIMIT_WINDOW_MS) {
      localStorage.removeItem(countKey(tournamentId))
      localStorage.removeItem(firstAtKey(tournamentId))
      return 0
    }
    const n = Number(localStorage.getItem(countKey(tournamentId)))
    return Number.isFinite(n) ? n : 0
  } catch { return 0 }
}

// Record a submission. If the rolling window has elapsed since the first
// recorded submission, this counts as a fresh start (count=1, firstAt=now).
function recordGuestSubmission(tournamentId) {
  if (!tournamentId) return 1
  try {
    const firstAt = Number(localStorage.getItem(firstAtKey(tournamentId))) || 0
    const now = Date.now()
    const windowElapsed = firstAt && now - firstAt >= PHOTO_LIMIT_WINDOW_MS
    const nextCount = (firstAt && !windowElapsed)
      ? (Number(localStorage.getItem(countKey(tournamentId))) || 0) + 1
      : 1
    if (!firstAt || windowElapsed) {
      localStorage.setItem(firstAtKey(tournamentId), String(now))
    }
    localStorage.setItem(countKey(tournamentId), String(nextCount))
    return nextCount
  } catch {
    return 1
  }
}
